import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Skeleton,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import Grid from "@mui/material/Grid2";
import {
  Business as BusinessIcon,
  Language as LanguageIcon,
  Stars as StarsIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useState, useRef, useMemo } from "react";
import Select from "react-select";
import useGetSubscriptionPlans from "@/hooks/useGetSubscriptionPlans";
import usePostJsonData from "@/hooks/postHooks/usePostJsonData";
import useGetOrganizationDetails from "@/hooks/useGetOrganizationDetails";
import axios from "@/api/axios";
import { useQueryClient } from "@tanstack/react-query";
import Toast from "@/components/toast";
import { useSession } from "@/hooks/useSession";
import { usePriceFormatter } from "@/hooks/usePriceFormatter";
import OrganizationHeader from "./components/OrganizationHeader";

const languageNames = {
  en: "English",
  tr: "Turkish",
  fr: "French",
  de: "German",
  es: "Spanish",
  pt: "Portuguese",
  nl: "Dutch",
  it: "Italian",
};

export default function Organization() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { token } = useSession();
  const { formatPrice } = usePriceFormatter();
  const {
    organizationDetails: orgData,
    organizationDetailsLoading: isLoading,
    organizationDetailsError: error,
  } = useGetOrganizationDetails();
  const [isUploading, setIsUploading] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [manageSubscriptionOpen, setManageSubscriptionOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const { plans, plansLoading } = useGetSubscriptionPlans(true);
  const { mutateAsync: postJson, isLoading: upgrading } = usePostJsonData();
  const currentPlan = useMemo(() => {
    if (!plans || !orgData?.subscription_plan_id) return null;
    return plans.find((p) => p.id === orgData.subscription_plan_id) || null;
  }, [plans, orgData?.subscription_plan_id]);
  const planOptions = useMemo(() => {
    if (!plans || !currentPlan) return [];

    let filteredPlans = [];

    if (currentPlan.subscription_channel === "INTERNAL") {
      // If current plan is INTERNAL, show only INTERNAL plans (regardless of currency)
      filteredPlans = plans.filter(
        (p) => p.subscription_channel === "INTERNAL"
      );
    } else if (currentPlan.subscription_channel === "IYZICO") {
      // If current plan is IYZICO, show only IYZICO plans with same currency
      filteredPlans = plans.filter((p) => {
        return (
          p.subscription_channel === "IYZICO" &&
          p.currency === currentPlan.currency
        );
      });
    } else {
      // Fallback: show all plans
      filteredPlans = plans;
    }

    // Ensure current plan is included (should already be included by the logic above)
    if (currentPlan && !filteredPlans.find((p) => p.id === currentPlan.id)) {
      filteredPlans.push(currentPlan);
    }

    // Sort plans to put current plan first
    return filteredPlans
      .sort((a, b) => {
        if (a.id === orgData?.subscription_plan_id) return -1;
        if (b.id === orgData?.subscription_plan_id) return 1;
        return a.internal_key.localeCompare(b.internal_key);
      })
      .map((p) => ({
        value: p.id,
        label: p.internal_key,
        isCurrentPlan: p.id === orgData?.subscription_plan_id,
        subscriptionChannel: p.subscription_channel,
        currency: p.currency,
      }));
  }, [plans, currentPlan, orgData?.subscription_plan_id]);
  const selectedPlanDetails = useMemo(() => {
    if (!plans || !selectedPlan?.value) return null;
    return plans.find((p) => p.id === selectedPlan.value) || null;
  }, [plans, selectedPlan?.value]);

  const handleToastClose = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  const handleCancelSubscription = async () => {
    if (!orgData?.subscription_id) return;

    try {
      setCanceling(true);
      await postJson({
        url: `/api/v1/subscriptions/${orgData.subscription_id}/cancel`,
        data: {},
      });

      setCancelConfirmOpen(false);
      setManageSubscriptionOpen(false);

      await queryClient.invalidateQueries({
        queryKey: ["organizationDetails"],
      });

      setToast({
        open: true,
        severity: "success",
        message: t(
          "organization.cancelSuccess",
          "Subscription cancelled successfully"
        ),
      });
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message:
          error.response?.data?.message ||
          t("organization.cancelError", "Failed to cancel subscription"),
      });
    } finally {
      setCanceling(false);
    }
  };

  const isInTrialPeriod = () => {
    if (!orgData?.trial_start_date || !orgData?.trial_end_date) return false;
    const now = new Date();
    const trialStart = new Date(orgData.trial_start_date);
    const trialEnd = new Date(orgData.trial_end_date);
    return now >= trialStart && now <= trialEnd;
  };

  const hasValidTrialPeriod = () => {
    if (!orgData?.trial_start_date || !orgData?.trial_end_date) return false;

    const trialStart = new Date(orgData.trial_start_date);
    const trialEnd = new Date(orgData.trial_end_date);

    // Check if start and end dates are the same (no actual trial period)
    return trialStart.getTime() !== trialEnd.getTime();
  };

  const getSubscriptionRenewalDate = () => {
    // Use the effective end date for renewal calculations
    return getEffectiveEndDate();
  };

  const getEffectiveEndDate = () => {
    if (!orgData?.start_date || !orgData?.end_date) return null;

    const startDate = new Date(orgData.start_date);
    const endDate = new Date(orgData.end_date);

    // Calculate the difference in years between start and end date
    const yearsDiff = endDate.getFullYear() - startDate.getFullYear();
    const monthsDiff = endDate.getMonth() - startDate.getMonth();
    const daysDiff = endDate.getDate() - startDate.getDate();

    // Calculate total difference in years (including partial years)
    const totalYearsDiff = yearsDiff + (monthsDiff + daysDiff / 30) / 12;

    // If the end date is more than 1 year from start date, use 1 year from start date
    if (totalYearsDiff > 1) {
      const oneYearFromStart = new Date(startDate);
      oneYearFromStart.setFullYear(startDate.getFullYear() + 1);
      return oneYearFromStart;
    }

    // For subscriptions within 1 year, use the original end date
    return endDate;
  };

  const getSubscriptionProgress = () => {
    if (!orgData?.start_date || !orgData?.end_date) return 0;

    const startDate = new Date(orgData.start_date);
    const effectiveEndDate = getEffectiveEndDate();
    const currentDate = new Date();

    if (!effectiveEndDate) return 0;

    // If current date is before start date, progress is 0
    if (currentDate < startDate) return 0;

    // If current date is after effective end date, progress is 100
    if (currentDate > effectiveEndDate) return 100;

    // Calculate progress percentage
    const totalDuration = effectiveEndDate.getTime() - startDate.getTime();
    const elapsedDuration = currentDate.getTime() - startDate.getTime();

    return Math.min(Math.max((elapsedDuration / totalDuration) * 100, 0), 100);
  };

  const getDaysRemaining = () => {
    const effectiveEndDate = getEffectiveEndDate();
    if (!effectiveEndDate) return null;

    const currentDate = new Date();

    // If subscription has ended
    if (currentDate > effectiveEndDate) return 0;

    const timeDiff = effectiveEndDate.getTime() - currentDate.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    return daysDiff;
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setToast({
        open: true,
        message: t(
          "organization.invalidFileType",
          "Please select an image file"
        ),
        severity: "error",
      });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setToast({
        open: true,
        message: t(
          "organization.fileTooLarge",
          "File size should be less than 2MB"
        ),
        severity: "error",
      });
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append("logo", file);

      await axios.put("/api/v1/org-admin/organizations/logo", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      try {
        // Invalidate the query which will trigger a refetch automatically
        await queryClient.invalidateQueries({
          queryKey: ["organizationDetails"],
        });

        setToast({
          open: true,
          message: t(
            "organization.logoUpdateSuccess",
            "Logo updated successfully"
          ),
          severity: "success",
        });
      } catch (invalidateError) {
        // If query invalidation fails, show error toast and log error
        console.error(
          "Failed to refresh organization details:",
          invalidateError
        );
        setToast({
          open: true,
          message: t(
            "organization.refreshError",
            "Logo uploaded but failed to refresh details. Please refresh the page."
          ),
          severity: "warning",
        });
      }

      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      const serverMsg = error.response?.data?.message;
      const errorMsg = serverMsg ?? error.message;

      setToast({
        open: true,
        severity: "error",
        message: t(
          "organization.logoUpdateError",
          { error: errorMsg },
          `Failed to update logo: ${errorMsg}`
        ),
      });
    } finally {
      setIsUploading(false);
    }
  };

  const renderLogo = () => {
    if (isLoading) {
      return (
        <Skeleton
          variant="circular"
          width={120}
          height={120}
          animation="wave"
        />
      );
    }

    return (
      <Box sx={{ position: "relative", textAlign: "left" }}>
        <Box
          sx={{
            position: "relative",
            display: "inline-block",
            "&:hover .logo-overlay": {
              opacity: 1,
            },
          }}
        >
          {!orgData?.logo_url ? (
            <Avatar
              sx={{
                width: 120,
                height: 120,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: theme.shadows[2],
              }}
            >
              <BusinessIcon sx={{ fontSize: 60 }} />
            </Avatar>
          ) : (
            <Avatar
              key={orgData.logo_url + (orgData.logo_updated_at || "")}
              src={orgData?.logo_url}
              alt={orgData?.name}
              sx={{
                width: 120,
                height: 120,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: theme.shadows[2],
                bgcolor: "white",
              }}
              slotProps={{
                img: {
                  alt: orgData?.name,
                  // style on the <img> itself (you can remove the '& img' rule from sx)
                  style: {
                    objectFit: "contain",
                    padding: 8,
                    width: "100%",
                    height: "100%",
                  },
                  // error handler belongs on the <img>, not the Avatar root
                  onError: (e) => {
                    e.currentTarget.src = ""; // trigger Avatar fallback
                    e.currentTarget.alt = t(
                      "organization.logoError",
                      "Logo failed to load"
                    );
                  },
                  // optional: prevent dragging the logo
                  draggable: false,
                },
              }}
            />
          )}

          {/* Logo Overlay */}
          <Box
            className="logo-overlay"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: alpha(theme.palette.common.black, 0.5),
              borderRadius: "50%",
              opacity: 0,
              transition: "opacity 0.2s ease",
              cursor: "pointer",
            }}
            onClick={() => !isUploading && fileInputRef.current?.click()}
          >
            <Tooltip
              title={
                isUploading
                  ? t("organization.uploading", "Uploading...")
                  : orgData?.logo_url
                    ? t("organization.changeLogo", "Change Logo")
                    : t("organization.uploadLogo", "Upload Logo")
              }
            >
              <span>
                <IconButton
                  size="small"
                  sx={{
                    color: "white",
                    bgcolor: alpha(theme.palette.common.white, 0.2),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.common.white, 0.3),
                    },
                    "&.Mui-disabled": {
                      color: "white",
                      bgcolor: alpha(theme.palette.common.white, 0.1),
                    },
                  }}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <EditIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <input
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleLogoUpload}
        />

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 2, display: "block", textAlign: "left" }}
        >
          {!orgData?.logo_url
            ? t("organization.noLogo", "No logo available")
            : t("organization.currentLogo", "Current organization logo")}
        </Typography>
      </Box>
    );
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {t("organization.errorLoading", "Error loading organization details")}
      </Alert>
    );
  }

  return (
    <>
      {/* Compact Enterprise Header */}
      <OrganizationHeader
        icon={SettingsIcon}
        title={t("organization.headerTitle", "Organization Details")}
        subtitle={t(
          "organization.headerSubtitle",
          "View and manage your organization's information"
        )}
      />

      {/* Organization Details Cards */}
      <Grid container spacing={3}>
        {/* Basic Information Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card
            elevation={0}
            sx={{
              height: "100%",
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.6)})`,
              backdropFilter: "blur(10px)",
            }}
          >
            <CardContent
              sx={{
                p: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                gutterBottom
                color="primary"
              >
                {t("organization.basicInfo", "Basic Information")}
              </Typography>

              {/* Logo Section */}
              <Box sx={{ textAlign: "center", position: "relative", mb: 3 }}>
                {renderLogo()}
              </Box>

              {isLoading ? (
                <Stack spacing={2}>
                  <Skeleton width="80%" height={32} />
                  <Skeleton width="60%" height={24} />
                </Stack>
              ) : (
                <Stack spacing={2}>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {t("organization.name", "Organization Name")}
                    </Typography>
                    <Typography variant="h6" fontWeight="medium">
                      {orgData.name}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {t("organization.acronym", "Organization ID")}
                    </Typography>
                    <Typography variant="body1">{orgData.acronym}</Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {t(
                        "organization.preferredLanguage",
                        "Preferred Language"
                      )}
                    </Typography>
                    <Chip
                      icon={<LanguageIcon />}
                      label={
                        languageNames[orgData.preferred_language_tag] ||
                        orgData.preferred_language_tag
                      }
                      size="small"
                      sx={{ fontWeight: "medium" }}
                    />
                  </Box>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Subscription Details Card */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card
            elevation={0}
            sx={{
              height: "100%",
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.6)})`,
              backdropFilter: "blur(10px)",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h6"
                fontWeight="bold"
                gutterBottom
                color="primary"
              >
                {t("organization.subscriptionDetails", "Subscription Details")}
              </Typography>
              {/* Upgrade button moved next to current plan below */}

              {isLoading ? (
                <Stack spacing={3}>
                  <Skeleton width="70%" height={60} />
                  <Skeleton width="100%" height={100} />
                  <Skeleton width="90%" height={80} />
                </Stack>
              ) : (
                <Stack spacing={3}>
                  {/* Current Plan */}
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      background: (theme) =>
                        theme.palette.mode === "light"
                          ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.main, 0.04)})`
                          : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)}, ${alpha(theme.palette.primary.main, 0.1)})`,
                      border: "1px solid",
                      borderColor: "primary.main",
                      borderOpacity: 0.2,
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: "primary.main",
                      },
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      spacing={3}
                      sx={{ justifyContent: "space-between" }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2.5}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: (theme) =>
                              theme.palette.mode === "light"
                                ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                                : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.primary.dark, 0.9)})`,
                            boxShadow: (theme) =>
                              `0 4px 12px ${alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.3 : 0.4)}`,
                          }}
                        >
                          <StarsIcon sx={{ fontSize: 28, color: "white" }} />
                        </Box>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            gutterBottom
                            sx={{ fontWeight: 500 }}
                          >
                            {t("organization.currentPlan", "Current Plan")}
                          </Typography>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                          >
                            <Typography
                              variant="h5"
                              color="primary.main"
                              sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}
                            >
                              {orgData.internal_key}
                            </Typography>
                            {currentPlan?.currency && (
                              <Chip
                                size="small"
                                label={currentPlan.currency}
                                sx={{
                                  height: 24,
                                  backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.1
                                  ),
                                  color: "primary.main",
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                }}
                              />
                            )}
                          </Stack>
                        </Box>
                      </Stack>
                      <Button
                        variant="contained"
                        onClick={() => setManageSubscriptionOpen(true)}
                        sx={{
                          px: { xs: 1.5, sm: 2, md: 3 },
                          py: 1,
                          borderRadius: 2,
                          textTransform: "none",
                          fontSize: {
                            xs: "0.8125rem",
                            sm: "0.875rem",
                            md: "0.9375rem",
                          },
                          fontWeight: 600,
                          minWidth: { xs: "auto", sm: "auto", md: "auto" },
                          boxShadow: (theme) =>
                            `0 4px 12px ${alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.3 : 0.4)}`,
                          background: (theme) =>
                            theme.palette.mode === "light"
                              ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                              : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.primary.dark, 0.9)})`,
                          "&:hover": {
                            background: (theme) =>
                              theme.palette.mode === "light"
                                ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`
                                : `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.9)}, ${alpha(theme.palette.primary.main, 0.9)})`,
                          },
                        }}
                        startIcon={
                          <Box
                            component="span"
                            sx={{
                              width: { xs: 20, sm: 22, md: 24 },
                              height: { xs: 20, sm: 22, md: 24 },
                              borderRadius: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "rgba(255, 255, 255, 0.2)",
                            }}
                          >
                            <SettingsIcon
                              sx={{ fontSize: { xs: 14, sm: 15, md: 16 } }}
                            />
                          </Box>
                        }
                      >
                        <Box
                          component="span"
                          sx={{
                            display: { xs: "none", sm: "none", md: "inline" },
                          }}
                        >
                          {t(
                            "organization.manageSubscription",
                            "Manage Subscription"
                          )}
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            display: { xs: "inline", sm: "inline", md: "none" },
                          }}
                        >
                          {t("organization.manageSubscriptionShort", "Manage")}
                        </Box>
                      </Button>
                    </Stack>
                  </Box>

                  {/* Trial Period */}
                  {orgData.trial_start_date && orgData.trial_end_date && (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.warning.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("organization.trialPeriod", "Trial Period")}
                      </Typography>
                      {hasValidTrialPeriod() ? (
                        <Stack direction="row" spacing={4}>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {t("organization.trialStart", "Start Date")}
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {new Date(
                                orgData.trial_start_date
                              ).toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {t("organization.trialEnd", "End Date")}
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {new Date(
                                orgData.trial_end_date
                              ).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Stack>
                      ) : (
                        <Typography
                          variant="body1"
                          fontWeight="medium"
                          color="text.secondary"
                        >
                          {t("organization.noTrialPeriod", "No trial period")}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Subscription Timeline */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {t("organization.timeline", "Subscription Timeline")}
                    </Typography>
                    <Stack spacing={2}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t(
                              "organization.subscriptionStartDate",
                              "Subscription Start"
                            )}
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {orgData.start_date
                              ? new Date(
                                  orgData.start_date
                                ).toLocaleDateString()
                              : "N/A"}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography variant="caption" color="text.secondary">
                            {t(
                              "organization.subscriptionEndDate",
                              "Subscription End"
                            )}
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {getEffectiveEndDate()
                              ? getEffectiveEndDate().toLocaleDateString()
                              : "N/A"}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Progress Information */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {t(
                            "organization.subscriptionProgress",
                            "Progress: {{progress}}%",
                            {
                              progress: Math.round(getSubscriptionProgress()),
                            }
                          )}
                        </Typography>
                        {getDaysRemaining() !== null && (
                          <Typography variant="caption" color="text.secondary">
                            {getDaysRemaining() > 0
                              ? t(
                                  "organization.daysRemaining",
                                  "{{days}} days remaining",
                                  {
                                    days: getDaysRemaining(),
                                  }
                                )
                              : t(
                                  "organization.subscriptionExpired",
                                  "Subscription expired"
                                )}
                          </Typography>
                        )}
                      </Box>

                      {/* Progress Bar */}
                      <Box
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          position: "relative",
                          overflow: "hidden",
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            bottom: 0,
                            width: `${getSubscriptionProgress()}%`,
                            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                            borderRadius: 3,
                            transition: "width 0.3s ease",
                            boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.3)}`,
                          }}
                        />
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Manage Subscription Dialog */}
      <Dialog
        open={manageSubscriptionOpen}
        onClose={() => setManageSubscriptionOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 24,
          sx: {
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            background: (theme) => theme.palette.background.default,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h5" component="div" fontWeight="600">
            {t("organization.manageSubscriptionTitle", "Manage Subscription")}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={4}>
            {/* Current Subscription Details */}
            <Box>
              <Typography
                variant="h6"
                color="primary"
                sx={{ mb: 2, fontWeight: 600 }}
              >
                {t("organization.subscriptionDetails", "Subscription Details")}
              </Typography>
              <Grid container spacing={3}>
                {/* Trial Period */}
                {orgData?.trial_start_date && orgData?.trial_end_date && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.info.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        color="info.main"
                        gutterBottom
                        sx={{ fontWeight: 600 }}
                      >
                        {t("organization.trialPeriod", "Trial Period")}
                      </Typography>
                      {hasValidTrialPeriod() ? (
                        <Stack spacing={1}>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {t("organization.trialStart", "Start Date")}
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {new Date(
                                orgData.trial_start_date
                              ).toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {t("organization.trialEnd", "End Date")}
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {new Date(
                                orgData.trial_end_date
                              ).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Stack>
                      ) : (
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          color="text.secondary"
                        >
                          {t("organization.noTrialPeriod", "No trial period")}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}

                {/* Subscription Period */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.success.main, 0.08),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="success.main"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {t(
                        "organization.subscriptionPeriod",
                        "Subscription Period"
                      )}
                    </Typography>
                    <Stack spacing={1}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t("organization.startDate", "Start Date")}
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {orgData?.start_date
                            ? new Date(orgData.start_date).toLocaleDateString()
                            : "N/A"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t("organization.endDate", "End Date")}
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {getSubscriptionRenewalDate()
                            ? getSubscriptionRenewalDate().toLocaleDateString()
                            : "N/A"}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Grid>
              </Grid>

              {/* Auto-renewal Notice */}
              {orgData?.end_date && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.warning.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                    mt: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    {t(
                      "organization.autoRenewalNotice",
                      "Subscription will be renewed automatically on {{renewalDate}} if not cancelled.",
                      {
                        renewalDate:
                          getSubscriptionRenewalDate()?.toLocaleDateString() ||
                          "",
                      }
                    )}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Available Plans */}
            <Box>
              <Typography
                variant="h6"
                color="primary"
                sx={{ mb: 2, fontWeight: 600 }}
              >
                {t("organization.availablePlans", "Available Plans")}
              </Typography>
              <Grid container spacing={2}>
                {planOptions.map((planOption) => {
                  const planDetails = plans?.find(
                    (p) => p.id === planOption.value
                  );
                  if (!planDetails) return null;

                  return (
                    <Grid key={planOption.value} size={{ xs: 12, sm: 6 }}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          bgcolor: planOption.isCurrentPlan
                            ? alpha(theme.palette.primary.main, 0.04)
                            : alpha(theme.palette.background.paper, 0.6),
                          borderColor: planOption.isCurrentPlan
                            ? "primary.main"
                            : "divider",
                          position: "relative",
                          ...(planOption.isCurrentPlan && {
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              height: 3,
                              background: "primary.main",
                              borderRadius: "8px 8px 0 0",
                            },
                          }),
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          sx={{ mb: 1 }}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight="600"
                            color="primary"
                          >
                            {planDetails.name}
                          </Typography>
                          <Stack direction="row" spacing={0.5}>
                            {planOption.isCurrentPlan && (
                              <Chip
                                size="small"
                                label={t(
                                  "organization.currentPlanLabel",
                                  "Current"
                                )}
                                color="primary"
                                sx={{ height: 20, fontSize: "0.625rem" }}
                              />
                            )}
                            <Chip
                              size="small"
                              label={
                                planDetails.subscription_channel === "IYZICO"
                                  ? t("organization.paymentMethodCard", "Card")
                                  : t(
                                      "organization.paymentMethodBankTransfer",
                                      "Bank Transfer"
                                    )
                              }
                              variant="outlined"
                              color={
                                planDetails.subscription_channel === "IYZICO"
                                  ? "secondary"
                                  : "success"
                              }
                              sx={{ height: 20, fontSize: "0.625rem" }}
                            />
                          </Stack>
                        </Stack>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          {planDetails.description}
                        </Typography>
                        <Stack spacing={2}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Box flex={1}>
                              <Typography
                                variant="h5"
                                fontWeight="700"
                                color="primary"
                                sx={{ mb: 0.5 }}
                              >
                                {formatPrice(planDetails.price)}{" "}
                                {planDetails.currency}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                              >
                                {t(
                                  "organization.billingInterval",
                                  "per {{interval}}",
                                  {
                                    interval:
                                      planDetails.billing_interval_count === 1
                                        ? planDetails.billing_interval?.toLowerCase()
                                        : `${planDetails.billing_interval_count} ${planDetails.billing_interval?.toLowerCase()}`,
                                  }
                                )}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                              >
                                <Chip
                                  size="small"
                                  label={planDetails.currency}
                                  variant="outlined"
                                  color="primary"
                                  sx={{ height: 24, fontSize: "0.75rem" }}
                                />
                                {planDetails.trial_days > 0 && (
                                  <Chip
                                    size="small"
                                    label={`${planDetails.trial_days} ${t("organization.trialDays", "trial days")}`}
                                    variant="outlined"
                                    color="info"
                                    sx={{ height: 24, fontSize: "0.75rem" }}
                                  />
                                )}
                              </Stack>
                            </Box>
                          </Stack>

                          {/* Plan Features Summary */}
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              bgcolor: alpha(
                                theme.palette.background.paper,
                                0.5
                              ),
                              border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={2}
                              justifyContent="space-between"
                            >
                              <Box sx={{ textAlign: "center" }}>
                                <Typography
                                  variant="h6"
                                  fontWeight="600"
                                  color="primary"
                                >
                                  {planDetails.max_users}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {t("organization.maxUsers", "Users")}
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: "center" }}>
                                <Typography
                                  variant="h6"
                                  fontWeight="600"
                                  color="primary"
                                >
                                  {planDetails.max_work_permits}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {t("organization.maxWorkPermits", "PTWs")}
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: "center" }}>
                                <Typography
                                  variant="h6"
                                  fontWeight="600"
                                  color="primary"
                                >
                                  {planDetails.max_observations}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {t(
                                    "organization.maxObservations",
                                    "Observations"
                                  )}
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        </Stack>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            background: (theme) => theme.palette.background.default,
            borderTop: "1px solid",
            borderColor: "divider",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() => setManageSubscriptionOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 1,
              textTransform: "none",
              px: 3,
            }}
          >
            {t("common.close", "Close")}
          </Button>
          <Stack direction="row" spacing={2}>
            <Button
              onClick={() => setCancelConfirmOpen(true)}
              variant="outlined"
              color="error"
              sx={{
                borderRadius: 1,
                textTransform: "none",
                px: 3,
              }}
            >
              {t("organization.cancelSubscription", "Cancel Plan")}
            </Button>
            <Button
              onClick={() => {
                setManageSubscriptionOpen(false);
                setUpgradeOpen(true);
              }}
              variant="contained"
              sx={{
                borderRadius: 1,
                textTransform: "none",
                px: 3,
              }}
            >
              {t("organization.upgradeButton", "Upgrade Plan")}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Cancel Subscription Confirmation Dialog */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 24,
          sx: {
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            background: (theme) => theme.palette.background.default,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="h5"
            component="div"
            fontWeight="600"
            color="error"
          >
            {t("organization.confirmCancellation", "Confirm Cancellation")}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.error.main, 0.08),
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              }}
            >
              <Typography variant="body1" color="text.primary">
                {isInTrialPeriod()
                  ? t(
                      "organization.cancelTrialMessage",
                      "Your subscription will be cancelled. You can continue trying the app until {{trialEndDate}}.",
                      {
                        trialEndDate: orgData?.trial_end_date
                          ? new Date(
                              orgData.trial_end_date
                            ).toLocaleDateString()
                          : "",
                      }
                    )
                  : t(
                      "organization.cancelSubscriptionMessage",
                      "Your subscription will be cancelled. You can continue using the app until {{endDate}}.",
                      {
                        endDate: orgData?.end_date
                          ? new Date(orgData.end_date).toLocaleDateString()
                          : "",
                      }
                    )}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {t(
                "organization.cancelWarning",
                "This action cannot be undone. Are you sure you want to proceed?"
              )}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            background: (theme) => theme.palette.background.default,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            onClick={() => setCancelConfirmOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 1,
              textTransform: "none",
              px: 3,
            }}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={handleCancelSubscription}
            variant="contained"
            color="error"
            disabled={canceling}
            sx={{
              borderRadius: 1,
              textTransform: "none",
              px: 3,
              minWidth: 120,
            }}
          >
            {canceling
              ? t("common.canceling", "Canceling...")
              : t("organization.confirmCancel", "Yes, Cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upgrade Subscription Dialog */}
      <Dialog
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 24,
          sx: {
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            background: (theme) => theme.palette.background.default,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h5" component="div" fontWeight="600">
            {t("organization.upgradeTitle", "Upgrade Subscription Plan")}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3}>
            {/* Current Plan Section */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                border: "1px solid",
                borderColor: "primary.main",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="600">
                    {currentPlan?.internal_key?.[0]?.toUpperCase() || "P"}
                  </Typography>
                </Box>
                <Box flex={1}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t("organization.currentPlan", "Current Plan")}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="600">
                    {currentPlan?.internal_key ?? orgData?.internal_key ?? ""}
                    {currentPlan?.currency && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 1, color: "text.secondary" }}
                      >
                        ({currentPlan.currency})
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Plan Selection */}
            <Box>
              <Typography
                variant="subtitle2"
                color="text.primary"
                sx={{ mb: 1, fontWeight: 600 }}
              >
                {t("organization.selectPlanLabel", "Select new plan")}
              </Typography>
              <Select
                isClearable
                isSearchable
                options={planOptions}
                value={selectedPlan}
                onChange={(opt) => setSelectedPlan(opt)}
                placeholder={t("organization.selectPlan", "Select a plan")}
                isLoading={plansLoading}
                menuPortalTarget={document.body}
                formatOptionLabel={(option) => (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography>{option.label}</Typography>
                    <Stack direction="row" spacing={0.5}>
                      {option.isCurrentPlan && (
                        <Chip
                          size="small"
                          label={t("organization.currentPlanLabel", "Current")}
                          color="primary"
                          variant="outlined"
                          sx={{
                            height: 20,
                            "& .MuiChip-label": {
                              px: 1,
                              fontSize: "0.625rem",
                              fontWeight: 600,
                            },
                          }}
                        />
                      )}
                      <Chip
                        size="small"
                        label={
                          option.subscriptionChannel === "IYZICO"
                            ? t("organization.paymentMethodCard", "Card")
                            : t(
                                "organization.paymentMethodBankTransfer",
                                "Bank Transfer"
                              )
                        }
                        variant="outlined"
                        color={
                          option.subscriptionChannel === "IYZICO"
                            ? "secondary"
                            : "success"
                        }
                        sx={{
                          height: 20,
                          "& .MuiChip-label": {
                            px: 1,
                            fontSize: "0.625rem",
                            fontWeight: 500,
                          },
                        }}
                      />
                      {option.currency && (
                        <Chip
                          size="small"
                          label={option.currency}
                          variant="outlined"
                          sx={{
                            height: 20,
                            "& .MuiChip-label": {
                              px: 1,
                              fontSize: "0.625rem",
                              fontWeight: 500,
                            },
                          }}
                        />
                      )}
                    </Stack>
                  </Box>
                )}
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 1301,
                  }),
                  control: (base) => ({
                    ...base,
                    borderRadius: 1,
                    minHeight: 48,
                    boxShadow: "none",
                    backgroundColor: theme.palette.background.paper,
                    borderColor: theme.palette.divider,
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                    },
                  }),
                  menu: (base) => ({
                    ...base,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow:
                      theme.palette.mode === "light"
                        ? "0 4px 20px rgba(0, 0, 0, 0.1)"
                        : "0 4px 20px rgba(0, 0, 0, 0.4)",
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: theme.palette.text.secondary,
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected
                      ? alpha(
                          theme.palette.primary.main,
                          theme.palette.mode === "light" ? 0.08 : 0.2
                        )
                      : state.isFocused
                        ? alpha(
                            theme.palette.action.hover,
                            theme.palette.mode === "light" ? 0.04 : 0.1
                          )
                        : "transparent",
                    color: state.isSelected
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                    fontWeight: state.isSelected ? 600 : 400,
                    "&:active": {
                      backgroundColor: alpha(
                        theme.palette.primary.main,
                        theme.palette.mode === "light" ? 0.12 : 0.3
                      ),
                    },
                  }),
                  input: (base) => ({
                    ...base,
                    color: theme.palette.text.primary,
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: theme.palette.text.primary,
                  }),
                }}
              />
            </Box>

            {selectedPlanDetails && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  bgcolor: alpha(theme.palette.background.paper, 0.6),
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {selectedPlanDetails.name} (
                      {selectedPlanDetails.internal_key})
                    </Typography>
                    {selectedPlanDetails.description && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {selectedPlanDetails.description}
                      </Typography>
                    )}
                  </Box>
                  <Stack alignItems="flex-end">
                    <Typography variant="h6" fontWeight={700} color="primary">
                      {formatPrice(selectedPlanDetails.price)}{" "}
                      {selectedPlanDetails.currency}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedPlanDetails.billing_interval_count}{" "}
                      {selectedPlanDetails.billing_interval?.toLowerCase() ||
                        ""}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  {Number.isFinite(selectedPlanDetails.trial_days) &&
                    selectedPlanDetails.trial_days > 0 && (
                      <Chip
                        size="small"
                        label={`${selectedPlanDetails.trial_days} ${t("organization.trialDays", "trial days")}`}
                      />
                    )}
                  {selectedPlanDetails.currency && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={selectedPlanDetails.currency}
                    />
                  )}
                </Stack>
              </Box>
            )}

            {/* Upgrade Note */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: (theme) =>
                  alpha(
                    theme.palette.info.main,
                    theme.palette.mode === "light" ? 0.05 : 0.1
                  ),
                border: "1px solid",
                borderColor: "info.main",
                borderOpacity: theme.palette.mode === "light" ? 0.3 : 0.4,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: 0.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: (theme) =>
                      alpha(
                        theme.palette.info.main,
                        theme.palette.mode === "light" ? 0.1 : 0.2
                      ),
                    color: "info.main",
                  }}
                >
                  <InfoIcon sx={{ fontSize: 16 }} />
                </Box>
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{
                    opacity: 0.9,
                    "& strong": {
                      color: "info.main",
                      fontWeight: 600,
                    },
                  }}
                >
                  <strong>Note:</strong>{" "}
                  {t(
                    "organization.upgradeNote",
                    "Upgrading will change your subscription plan immediately."
                  )}
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            background: (theme) => theme.palette.background.default,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            onClick={() => setUpgradeOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 1,
              textTransform: "none",
              px: 3,
            }}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={
              !selectedPlan ||
              upgrading ||
              selectedPlan?.value === orgData?.subscription_plan_id
            }
            onClick={async () => {
              try {
                await postJson({
                  url: "/api/v1/subscriptions/upgrade",
                  data: { new_subscription_plan_id: selectedPlan.value },
                });
                setUpgradeOpen(false);
                setSelectedPlan(null);
                await queryClient.invalidateQueries({
                  queryKey: ["organizationDetails"],
                });
                setToast({
                  open: true,
                  severity: "success",
                  message: t(
                    "organization.upgradeSuccess",
                    "Subscription upgraded successfully"
                  ),
                });
              } catch (error) {
                setToast({
                  open: true,
                  severity: "error",
                  message:
                    error.response?.data?.message ||
                    t(
                      "organization.upgradeError",
                      "Failed to upgrade subscription"
                    ),
                });
              }
            }}
            sx={{
              borderRadius: 1,
              textTransform: "none",
              px: 3,
              minWidth: 120,
            }}
          >
            {upgrading
              ? t("common.upgrading", "Upgrading...")
              : t("organization.upgradeConfirmTo", "Upgrade to {{plan}}", {
                  plan: selectedPlan?.label || "",
                })}
          </Button>
        </DialogActions>
      </Dialog>

      <Toast
        open={toast.open}
        severity={toast.severity}
        message={toast.message}
        onClose={handleToastClose}
      />
    </>
  );
}
